import get from 'lodash/get';

import { normalizeSystemDiskRecord } from '../../../capabilities/image-vm/utils';
import { deserializeServiceListFromApi } from '../../../capabilities/service/serviceApiField';
import { filterConfiguredSharedVolumes, normalizeVolumeList } from '../../../capabilities/shared-volume/utils';
import { normalizeDataDiskList } from '../../../capabilities/storage/utils';
import { readPricingMeta } from '../../../components/vendorDevice';
import type { PipelineContext } from '../../../core/types';
import { buildSystemOptionsFromForm } from '../../../platform/container-deploy-config';
import { buildDefaultDirectResourceModules } from '../../../platform/scene-layout/utils/resolveModuleDefaults';
import { hasMeaningfulValue } from '../../../utils';
import { readFormArchitecture } from '../../_shared/common/formHelpers';
import { resolveEnabledModulesForSpecFilter } from '../../_shared/common/sku/resolveEnabledModulesForSpecFilter';
import { filterVmSpecByEnabledModules } from './enabledModules';
import { hasConfiguredGpu } from './gpu';
import type { VmSpec } from './schema';
import { flattenDnsNameservers, flattenDnsSearches, mergeVmSystem, toFormDnsNameservers, toFormDnsSearches } from './volumes';

function readOptionalPositiveNumber(value: unknown): number | undefined {
  if (!hasMeaningfulValue(value)) return undefined;
  const num = Number(value);
  return Number.isFinite(num) && num > 0 ? num : undefined;
}

function readGpu(raw: unknown) {
  if (!hasConfiguredGpu(raw)) return undefined;
  const gpu = raw as Record<string, unknown>;
  const quantityUnit = gpu.quantityUnit != null && String(gpu.quantityUnit).trim() !== '' ? String(gpu.quantityUnit).trim() : undefined;
  return {
    vendor: gpu.vendor as string | undefined,
    device: gpu.device as string | undefined,
    vendorID: (gpu.vendorID ?? gpu.vendorId) as string | undefined,
    deviceID: (gpu.deviceID ?? gpu.deviceId) as string | undefined,
    number: gpu.number as number | string | undefined,
    name: gpu.name as string | undefined,
    code: gpu.code as string | undefined,
    deviceType: gpu.deviceType as string | undefined,
    ...(quantityUnit ? { quantityUnit } : {}),
  };
}

function readVmSystem(values: Record<string, unknown>): VmSpec['system'] {
  const formSystem = get(values, 'system') as Record<string, unknown> | undefined;
  const image = get(values, 'systemDisk.image') as Record<string, unknown> | undefined;
  const systemOptions = buildSystemOptionsFromForm(values);

  return mergeVmSystem(
    image
      ? {
          os: image.os as string | undefined,
          version: image.version as string | undefined,
          bootloader: image.bootloader as string | undefined,
        }
      : undefined,
    {
      architecture: readFormArchitecture(values),
      os: formSystem?.os as string | undefined,
      version: formSystem?.version as string | undefined,
      bootloader: formSystem?.bootloader as string | undefined,
      ...(systemOptions ? { options: systemOptions } : {}),
    },
  );
}

function readFormMemory(memory: VmSpec['resource']['memory']): number | string | undefined {
  if (!hasMeaningfulValue(memory)) return undefined;
  const text = String(memory).trim();
  const numeric = Number(text.replace(/Gi$/i, ''));
  if (Number.isFinite(numeric) && numeric > 0) return numeric;
  return memory;
}

function readFormNetworkName(value: string | undefined): string | undefined {
  if (!hasMeaningfulValue(value)) return undefined;
  const text = String(value).trim();
  return text || undefined;
}

function readFormNetworkRef(ref?: { name?: string }): { name: string } | undefined {
  const name = readFormNetworkName(ref?.name);
  return name ? { name } : undefined;
}

function mergeFormSystem(spec: VmSpec): VmSpec['system'] {
  const image = spec.systemDisk?.image;
  const formSystem = spec.system;

  return mergeVmSystem(
    image
      ? {
          os: image.os,
          version: image.version,
          bootloader: image.bootloader,
        }
      : undefined,
    {
      ...formSystem,
      architecture: formSystem?.architecture?.trim() || undefined,
      ...(formSystem?.options ? { options: formSystem.options } : {}),
    },
  );
}

export function vmFromForm(values: Record<string, unknown>, ctx: PipelineContext): VmSpec {
  const cpu = get(values, 'resource.cpu', {}) as Record<string, unknown>;
  const network = get(values, 'resource.network', {}) as Record<string, unknown>;
  const dnsConfig = get(network, 'dnsConfig', {}) as Record<string, unknown>;

  const optionMeta = get(values, 'optionMeta');
  const spec: VmSpec = {
    name: String(values.name ?? ''),
    description: values.description ? String(values.description) : undefined,
    runStrategy: String(values.runStrategy ?? ''),
    workspace: values.workspace ? String(values.workspace) : undefined,
    datacenter: values.datacenter ? String(values.datacenter) : undefined,
    ...(optionMeta && typeof optionMeta === 'object' && !Array.isArray(optionMeta)
      ? { optionMeta: optionMeta as Record<string, unknown> }
      : {}),
    system: readVmSystem(values),
    image: values.image ? String(values.image) : undefined,
    source: values.source,
    resource: {
      memory: readOptionalPositiveNumber(get(values, 'resource.memory')),
      cpu: {
        physicalCards: readOptionalPositiveNumber(cpu.physicalCards),
        physicalCores: readOptionalPositiveNumber(cpu.physicalCores),
        logicalCores: readOptionalPositiveNumber(cpu.logicalCores),
        pricing: readPricingMeta(cpu.pricing),
      },
      gpu: readGpu(get(values, 'resource.gpu')),
      gpuPricing: readPricingMeta(get(values, 'resource.gpuPricing')),
      memoryPricing: readPricingMeta(get(values, 'resource.memoryPricing')),
      cpuPricing: readPricingMeta(get(values, 'resource.cpuPricing')),
      network: {
        vpc: get(network, 'vpc.name') ? { name: String(get(network, 'vpc.name')) } : undefined,
        subnet: { name: String(get(network, 'subnet.name') ?? 'default') },
        securitygroup: get(network, 'securitygroup.name') ? { name: String(get(network, 'securitygroup.name')) } : undefined,
        Bandwidth: get(network, 'Bandwidth') as string | number | undefined,
        bandwidthPricing: readPricingMeta(get(network, 'bandwidthPricing')),
        dnsConfig: {
          nameservers: flattenDnsNameservers(dnsConfig.nameservers),
          searches: flattenDnsSearches(dnsConfig.searches),
          options: Array.isArray(dnsConfig.options) ? (dnsConfig.options as { name: string; value: string }[]) : undefined,
        },
        hpc: get(network, 'hpc') as VmSpec['resource']['network']['hpc'],
        service: Array.isArray(network.service) ? (network.service as VmSpec['resource']['network']['service']) : undefined,
      },
    },
    systemDisk: normalizeSystemDiskRecord(values.systemDisk),
    dataDisks: normalizeDataDiskList(values.dataDisks),
    volumes: filterConfiguredSharedVolumes(normalizeVolumeList(values.volumes), 'pvcInfo'),
  };

  const enabledModules = resolveEnabledModulesForSpecFilter(ctx, values);
  return filterVmSpecByEnabledModules(spec, enabledModules, ctx.sku?.skuType);
}

export function vmToForm(spec: VmSpec, _ctx?: PipelineContext): Record<string, unknown> {
  const dns = spec.resource.network.dnsConfig;
  const vpcName = readFormNetworkName(spec.resource.network.vpc.name);
  return {
    name: spec.name,
    description: spec.description,
    runStrategy: spec.runStrategy,
    workspace: spec.workspace,
    datacenter: spec.datacenter,
    ...(spec.optionMeta ? { optionMeta: spec.optionMeta } : {}),
    system: mergeFormSystem(spec),
    image: spec.image,
    source: spec.source,
    systemDisk: spec.systemDisk,
    dataDisks: spec.dataDisks ?? [],
    volumes: spec.volumes ?? [],
    resource: {
      memory: readFormMemory(spec.resource.memory),
      cpu: {
        physicalCards: spec.resource.cpu.physicalCards,
        physicalCores: spec.resource.cpu.physicalCores,
        logicalCores: spec.resource.cpu.logicalCores,
        pricing: spec.resource.cpu.pricing,
      },
      gpu: spec.resource.gpu,
      gpuPricing: spec.resource.gpuPricing,
      memoryPricing: spec.resource.memoryPricing,
      cpuPricing: spec.resource.cpuPricing,
      network: {
        vpc: vpcName ? { name: vpcName } : {},
        subnet: { name: spec.resource.network.subnet.name ?? 'default' },
        securitygroup: readFormNetworkRef(spec.resource.network.securitygroup),
        Bandwidth: spec.resource.network.Bandwidth,
        bandwidthPricing: spec.resource.network.bandwidthPricing,
        dnsConfig: {
          nameservers: toFormDnsNameservers(dns?.nameservers),
          searches: toFormDnsSearches(dns?.searches),
          options: dns?.options ?? [],
        },
        hpc: spec.resource.network.hpc,
        service: deserializeServiceListFromApi(spec.resource.network.service as unknown[] | undefined),
      },
    },
  };
}

export function vmGetInitialFormValues(ctx?: PipelineContext): Record<string, unknown> {
  const datacenter = ctx?.cluster;
  return {
    ...(datacenter ? { datacenter } : {}),
    skuResourceModules: buildDefaultDirectResourceModules('VirtualMachine'),
    resource: {
      network: {
        subnet: { name: 'default' },
        dnsConfig: {
          nameservers: [],
          searches: [],
          options: [],
        },
        hpc: { elasticRdma: [] },
      },
    },
    dataDisks: [],
    volumes: [],
  };
}

export function isVmMemoryReady(memory: VmSpec['resource']['memory']): boolean {
  if (!hasMeaningfulValue(memory)) return false;
  const num = Number(memory);
  return Number.isFinite(num) && num > 0;
}

export function readVmCpuTotal(cpu: VmSpec['resource']['cpu']): number | null {
  const { physicalCards, physicalCores, logicalCores } = cpu;
  if (physicalCards == null || physicalCores == null || logicalCores == null) return null;
  if (![physicalCards, physicalCores, logicalCores].every((value) => Number.isFinite(value) && value > 0)) {
    return null;
  }
  return physicalCards * physicalCores * logicalCores;
}
